import { createRoot, type Root } from 'react-dom/client';
import { configStore, reset as resetConfigStore } from '@config/config-store';
import { App } from './app';

let reactRoot: Root | null;

/** This function is called by the host app to inject the app into the host app's dom.
 *
 * @param {string} elementSelector - The selector for the element in the host app's dom where the app should be injected, for example `#app`.
 * @param {object} options - Options for the injection.
 */
export function inject(
  elementSelector: string,
  {
    hostingBasePath,
    routingBasePath,
  }: {
    hostingBasePath?: string;
    routingBasePath?: string;
  } = {}
) {
  if (typeof elementSelector !== 'string') {
    throw new TypeError('elementSelector must be a string');
  }

  if (
    hostingBasePath &&
    (typeof hostingBasePath !== 'string' || !hostingBasePath.endsWith('/'))
  ) {
    throw new TypeError(
      'hostingBasePath must be a string that ends with trailing slash, if provided'
    );
  }

  if (routingBasePath && typeof routingBasePath !== 'string') {
    throw new TypeError('routingBasePath must be a string, if provided');
  }

  if (reactRoot) {
    cleanup(elementSelector);
  }

  resetConfigStore();

  /* Setup configuration values from passed in options */
  if (hostingBasePath) {
    configStore.hostingBasePath = hostingBasePath;
  }

  if (routingBasePath) {
    configStore.routingBasePath = routingBasePath;
  }

  const appContainer = document.querySelector(elementSelector);

  if (!appContainer) {
    throw new Error(`Element not found: ${elementSelector}`);
  }

  reactRoot = createRoot(appContainer, {
    identifierPrefix: 'pharmacy-portal',
  });
  reactRoot.render(<App />);
}

export function cleanup(elementSelector: string) {
  reactRoot?.unmount?.();
  reactRoot = null;

  const container = document.querySelector(elementSelector);
  container?.replaceChildren?.();

  resetConfigStore();
}
